/**
 * Calendar List Component
 * Handles the main calendar view with filter interactions, AJAX updates, and load more functionality
 */

import {
    setupFilterDropdowns,
    setupCustomDatePicker,
    getActiveFilters,
    renderChips,
    updateCounter,
    buildFilterParams,
    createEventCardHTML,
    populateFiltersFromURL,
    getFilterLabelDatePlaceholder
} from './calendarShared.js';

function initCalendarList(list) {
    if (list.dataset.calendarInitialized) return;
    list.dataset.calendarInitialized = 'true';

    const ajaxUrl = list.dataset.ajaxUrl;
    const stiftId = list.dataset.stiftId;
    const nodeId = list.dataset.nodeId;
    const perPage = parseInt(list.dataset.perPage || '12');

    const checkboxes = list.querySelectorAll('input[type="checkbox"]');
    const clearButton = list.querySelector('.calendar-list__clear-filters');
    const gridElement = list.querySelector('.calendar-list__grid');
    const loadMoreButton = list.querySelector('.calendar-list__cta');

    let currentOffset = 0;
    let totalCount = 0;

    // Initialize filter dropdowns
    setupFilterDropdowns(list, 'calendar-list');

    // Initialize custom date picker
    setupCustomDatePicker(list, 'calendar-list', onFilterChange);

    // Handle filter changes
    function onFilterChange() {
        currentOffset = 0; // Reset offset when filters change
        updateActiveFilters();
        fetchResults(true); // Replace grid
    }

    checkboxes.forEach(checkbox => {
        checkbox.addEventListener('change', onFilterChange);
    });

    // Clear all filters
    if (clearButton) {
        clearButton.addEventListener('click', function() {
            checkboxes.forEach(cb => cb.checked = false);
            const dateInput = list.querySelector('input[name="fromDate"]');
            if (dateInput) {
                dateInput.value = '';
                const button = list.querySelector('.calendar-list__date-picker .calendar-list__filter-button span');
                if (button) button.textContent = getFilterLabelDatePlaceholder(list);
            }
            onFilterChange();
        });
    }

    // Update active filter chips display
    function updateActiveFilters() {
        const activeFilters = getActiveFilters(list);
        renderChips(activeFilters, list, 'calendar-list', onFilterChange);
    }

    // Load more button handler
    if (loadMoreButton) {
        loadMoreButton.addEventListener('click', function(e) {
            currentOffset += perPage;
            fetchResults(false); // Append to grid
            // Remove focus after click to return button to initial state
            e.currentTarget.blur();
        });
    }

    // Fetch filtered results via AJAX
    function fetchResults(replace = true) {
        const filters = getActiveFilters(list);
        const params = buildFilterParams(filters, {
            nodeId: nodeId,
            from: currentOffset
        });

        // Build URL
        const separator = ajaxUrl.includes('?') ? '&' : '?';
        const url = params.toString() ? `${ajaxUrl}${separator}${params.toString()}` : ajaxUrl;

        console.log('CalendarList: Fetching results', {
            ajaxUrl: ajaxUrl,
            nodeId: nodeId,
            filters: filters,
            currentOffset: currentOffset,
            replace: replace,
            finalUrl: url
        });

        fetch(url)
            .then(response => response.json())
            .then(data => {
                console.log('CalendarList: Data received', {
                    total: data.total,
                    itemsCount: data.items.length,
                    hasMore: data.hasMore
                });

                totalCount = data.total;

                // Update count
                updateCounter(data.total, list, 'calendar-list');

                // Update grid with new items
                if (gridElement) {
                    const eventDetailBaseUrl = list.dataset.eventDetailUrl || '';

                    if (replace) {
                        // Replace grid content (filter change)
                        if (data.items.length === 0) {
                            gridElement.innerHTML = '<div class="calendar-list__no-results">Ingen arrangementer matcher dine filtre</div>';
                        } else {
                            gridElement.innerHTML = data.items.map(item => createEventCardHTML(item, eventDetailBaseUrl)).join('');
                        }
                    } else {
                        // Append to grid (load more)
                        const newHTML = data.items.map(item => createEventCardHTML(item, eventDetailBaseUrl)).join('');
                        gridElement.insertAdjacentHTML('beforeend', newHTML);
                    }
                }

                // Show/hide load more button
                if (loadMoreButton) {
                    const footer = loadMoreButton.parentElement;
                    if (data.hasMore) {
                        footer.style.display = 'flex';
                    } else {
                        footer.style.display = 'none';
                    }
                }
            })
            .catch(error => {
                console.error('Error fetching calendar results:', error);
            });
    }

    // Pre-populate filters from URL params on page load
    populateFiltersFromURL(list, 'calendar-list');

    // Always trigger initial AJAX fetch
    updateActiveFilters();
    fetchResults(true);
}

document.addEventListener('DOMContentLoaded', function() {
    document.querySelectorAll('[data-component="CalendarList"]').forEach(initCalendarList);

    new MutationObserver(function(mutations) {
        for (const mutation of mutations) {
            for (const node of mutation.addedNodes) {
                if (node.nodeType !== 1) continue;
                if (node.matches('[data-component="CalendarList"]')) {
                    initCalendarList(node);
                }
                node.querySelectorAll('[data-component="CalendarList"]').forEach(initCalendarList);
            }
        }
    }).observe(document.body, { childList: true, subtree: true });
});
